import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { MupLayout, StateBadge } from "@/pages/mup/MupLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FileCog, Download, Send, PenLine, Ban, Pencil, ShieldCheck,
  Clock, FileText, Building2, User, Package,
} from "lucide-react";
import ClienteCollegati from "@/components/ClienteCollegati";
import { RemoteLinkButton, FascicoloButton } from "@/pages/mup/MupRemoteActions";

export default function MupPraticaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpDest, setOtpDest] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const load = useCallback(() => {
    api.get(`/mup/pratiche/${id}`).then(({ data }) => setP(data)).catch(() => navigate("/mup/pratiche"));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(ok); load(); return r; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const generate = () => act(() => api.post(`/mup/pratiche/${id}/generate`), "PDF generato");
  const cancel = () => act(() => api.post(`/mup/pratiche/${id}/cancel`), "Pratica annullata");
  const send = async () => {
    const r = await act(() => api.post(`/mup/pratiche/${id}/send`), "Fascicolo inviato · OTP via SMS");
    if (r?.data?.destinazione) {
      setOtpDest(r.data.destinazione);
      setResendIn(r.data.cooldown || 60);
      setOtp("");
      setSignOpen(true);
    }
  };
  const resendOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mup/pratiche/${id}/resend-otp`);
      setOtpDest(data.destinazione || otpDest);
      setResendIn(data.cooldown || 60);
      toast.success(`Nuovo OTP inviato via SMS a ${data.destinazione || "il cliente"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openSign = () => { setOtp(""); setOtpDest(p?.signature?.destinazione || ""); setSignOpen(true); };
  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/mup/pratiche/${id}/sign`, { otp });
      toast.success("Firma completata");
      setSignOpen(false); setOtp(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openPdf = () => window.open(`${API}/mup/pratiche/${id}/pdf`, "_blank");

  const [rinG, setRinG] = useState("");
  const [rinData, setRinData] = useState("");
  const [rinMotivo, setRinMotivo] = useState("");
  const addRinuncia = async () => {
    if (!rinG.trim()) { toast.error("Indica la garanzia"); return; }
    await act(() => api.post(`/mup/pratiche/${id}/rinunce`, { garanzia: rinG, data: rinData, motivo: rinMotivo }), "Annotazione aggiunta al fascicolo");
    setRinG(""); setRinData(""); setRinMotivo("");
  };
  const delRinuncia = (rid) => act(() => api.delete(`/mup/pratiche/${id}/rinunce/${rid}`), "Annotazione rimossa");

  // Firma OTP dell'appendice garanzie
  const [apxOpen, setApxOpen] = useState(false);
  const [apxOtp, setApxOtp] = useState("");
  const [apxDest, setApxDest] = useState("");
  const [apxResendIn, setApxResendIn] = useState(0);
  useEffect(() => {
    if (apxResendIn <= 0) return;
    const t = setInterval(() => setApxResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [apxResendIn]);
  const sendApxOtp = async () => {
    const r = await act(() => api.post(`/mup/pratiche/${id}/rinunce/send-otp`), "OTP inviato al cliente via SMS");
    if (r?.data?.destinazione) { setApxDest(r.data.destinazione); setApxResendIn(r.data.cooldown || 60); setApxOtp(""); setApxOpen(true); }
  };
  const resendApxOtp = async () => {
    const r = await act(() => api.post(`/mup/pratiche/${id}/rinunce/resend-otp`), "Nuovo OTP inviato via SMS");
    if (r?.data?.destinazione) { setApxDest(r.data.destinazione); setApxResendIn(r.data.cooldown || 60); }
  };
  const signApx = async () => {
    await act(() => api.post(`/mup/pratiche/${id}/rinunce/sign`, { otp: apxOtp }), "Appendice firmata dal cliente");
    setApxOpen(false); setApxOtp("");
  };
  const prepareApxLink = async () => {
    const r = await act(() => api.post(`/mup/pratiche/${id}/rinunce/prepare-link`), "Link firma generato");
    const link = r?.data?.sign_link;
    if (link) {
      const url = `${window.location.origin}${link}`;
      try { await navigator.clipboard.writeText(url); toast.success("Link copiato: invialo al cliente"); }
      catch { toast.info(url); }
    }
  };

  if (!p) return <MupLayout title="Caricamento…"><div /></MupLayout>;

  const cd = p.client_data || {};
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; } };

  return (
    <MupLayout
      subtitle={p.code}
      title={cd.name || "Pratica"}
      actions={<StateBadge state={p.state} />}
    >
      {/* Action bar */}
      <div className="flex gap-2 flex-wrap mb-6">
        {p.state !== "firmato" && (
          <Button data-testid="mup-edit-btn" variant="outline" onClick={() => navigate(`/mup/pratiche/${id}/modifica`)}>
            <Pencil className="h-4 w-4 mr-2" /> Modifica
          </Button>
        )}
        {["bozza", "pronto_invio"].includes(p.state) && (
          <Button data-testid="mup-generate-btn" variant="outline" disabled={busy} onClick={generate}>
            <FileCog className="h-4 w-4 mr-2" /> Genera PDF
          </Button>
        )}
        <Button data-testid="mup-download-btn" variant="outline" onClick={openPdf} disabled={!p.hash}>
          <Download className="h-4 w-4 mr-2" /> Apri PDF
        </Button>
        {p.state === "pronto_invio" && (
          <Button data-testid="mup-send-btn" disabled={busy} onClick={send} className="brand-gradient text-white border-0">
            <Send className="h-4 w-4 mr-2" /> Firma con il cliente (OTP)
          </Button>
        )}
        {["pronto_invio", "inviato"].includes(p.state) && <RemoteLinkButton p={p} onDone={load} />}
        <FascicoloButton p={p} />
        {p.state === "inviato" && (
          <Button data-testid="mup-sign-open-btn" disabled={busy} onClick={openSign} className="brand-gradient text-white border-0">
            <PenLine className="h-4 w-4 mr-2" /> Firma OTP
          </Button>
        )}
        {!["firmato", "annullato"].includes(p.state) && (
          <Button data-testid="mup-cancel-btn" variant="outline" disabled={busy} onClick={cancel} className="text-red-600 border-red-200 hover:bg-red-50">
            <Ban className="h-4 w-4 mr-2" /> Annulla
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Dettagli pratica" icon={FileText}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <KV icon={User} k="Cliente" v={cd.name} />
              <KV k="CF / P.IVA" v={cd.fiscal_code || cd.vat} />
              <KV icon={Building2} k="Compagnia" v={p.company_name} />
              <KV icon={Package} k="Prodotto" v={p.product_name} />
              <KV k="Intermediario" v={p.intermediary_name} />
              <KV k="N. proposta/polizza" v={p.proposal_number} />
              <KV k="Data compilazione" v={p.compilation_date} />
              <KV k="Canale distribuzione" v={p.distribution_channel} />
              <KV k="Consegna" v={cd.delivery_pref} />
              <KV k="Firma" v={p.otp_signature ? "Elettronica con OTP" : "Cartacea"} />
            </div>
          </Card>

          <ClienteCollegati currentModule="mup" currentId={id}
            anagraficaId={p.client_id} nome={cd?.name} piva={cd?.vat} />

          <Card title="Valutazione del prodotto" icon={ShieldCheck}>
            {p.area_titolo && (
              <p className="text-sm text-slate-600 mb-3"><b>Area di tutela:</b> {p.area_titolo}</p>
            )}
            <div className="overflow-x-auto">
              {(p.needs_grid || []).length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 font-medium">Voce</th>
                      <th className="py-2 font-medium">Richieste ed esigenze</th>
                      <th className="py-2 font-medium">Proposte interm.</th>
                      <th className="py-2 font-medium">Confermate cliente</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {p.needs_grid.map((r, i) => (
                      <tr key={i}>
                        <td className="py-1.5 text-slate-900">{r.voce}</td>
                        <td className="py-1.5 text-center">{YN(r.richieste)}</td>
                        <td className="py-1.5 text-center">{YN(r.proposte)}</td>
                        <td className="py-1.5 text-center">{YN(r.confermate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2 font-medium">Voce</th>
                      <th className="py-2 font-medium">Richieste</th>
                      <th className="py-2 font-medium">Proposte</th>
                      <th className="py-2 font-medium">Confermate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(p.grid || []).map((r, i) => (
                      <tr key={i}>
                        <td className="py-1.5 text-slate-900">{r.voce}</td>
                        <td className="py-1.5 text-center">{TRI(r.richieste)}</td>
                        <td className="py-1.5 text-center">{TRI(r.proposte)}</td>
                        <td className="py-1.5 text-center">{TRI(r.confermate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {(p.prodotto_rows || []).filter((r) => r.selected !== false).length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Prodotto — trasparenza remunerazioni</h4>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left p-2 font-medium">Compagnia</th>
                        <th className="text-left p-2 font-medium">Prodotto</th>
                        <th className="p-2 font-medium">Vend. esclusiva</th>
                        <th className="p-2 font-medium">Onorario diretto</th>
                        <th className="p-2 font-medium">Commiss. inclusa</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {p.prodotto_rows.filter((r) => r.selected !== false).map((r, i) => (
                        <tr key={i}>
                          <td className="p-2 text-slate-900">{r.compagnia || "—"}</td>
                          <td className="p-2 text-slate-900">{r.nome_prodotto || "—"}</td>
                          <td className="p-2 text-center">{r.vendita_esclusiva === "si" ? "Sì" : "No"}</td>
                          <td className="p-2 text-center">{r.onorario_diretto === "si" ? "Sì" : "No"}</td>
                          <td className="p-2 text-center">{r.commissione_inclusa === "si" ? "Sì" : "No"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {p.consulenza && (p.consulenza.art119ter_c3 || p.consulenza.art119ter_c4) && (
              <p className="text-sm text-slate-600 mt-3">
                <b>Consulenza:</b> {p.consulenza.art119ter_c3 ? "raccomandazione personalizzata (art. 119-ter c.3)" : ""}
                {p.consulenza.art119ter_c3 && p.consulenza.art119ter_c4 ? " · " : ""}
                {p.consulenza.art119ter_c4 ? "analisi imparziale e personale (art. 119-ter c.4)" : ""}
              </p>
            )}
            {"dichiarazione_coerenza" in p && (
              <p className="text-sm mt-1"><b>Dichiarazione di coerenza:</b>{" "}
                <span className={p.dichiarazione_coerenza ? "text-emerald-600" : "text-red-500"}>
                  {p.dichiarazione_coerenza ? "Il prodotto è coerente" : "Non confermata"}
                </span>
              </p>
            )}
            {p.note_intermediary && <p className="text-sm text-slate-600 mt-3"><b>Note intermediario:</b> {p.note_intermediary}</p>}
            {p.note_client && <p className="text-sm text-slate-600 mt-1"><b>Note cliente:</b> {p.note_client}</p>}
            {p.note_internal && <p className="text-sm text-amber-600 mt-1"><b>Note interne (non nel fascicolo):</b> {p.note_internal}</p>}
          </Card>

          <Card title="Garanzie non inserite dopo la firma" icon={ShieldCheck}>
            <p className="text-xs text-slate-500 mb-3">
              Annota qui le garanzie acconsentite nel MUP ma <b>non inserite in polizza</b> su decisione del cliente <b>dopo la firma</b>.
              L'annotazione viene aggiunta come <b>appendice al fascicolo</b> (con data e motivo) e non modifica il MUP già firmato.
            </p>
            {p.rinunce_signature?.signed_at && (
              <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-sm text-emerald-700" data-testid="mup-apx-signed">
                ✔ Appendice <b>firmata dal cliente</b> il <b>{fmt(p.rinunce_signature.signed_at)}</b> · tx {p.rinunce_signature.transaction_id?.slice(0, 10)}… — l'attestato di firma è incluso nel PDF del fascicolo.
              </div>
            )}
            {(p.garanzie_rinunciate || []).length > 0 ? (
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 mb-3">
                {p.garanzie_rinunciate.map((r) => (
                  <div key={r.id} data-testid={`mup-rinuncia-${r.id}`} className="flex items-start justify-between gap-3 p-2.5 text-sm">
                    <div>
                      <div className="text-slate-900 font-medium">{r.garanzia}</div>
                      <div className="text-xs text-slate-500">{dIT(r.data)}{r.motivo ? ` · ${r.motivo}` : ""}</div>
                    </div>
                    <button type="button" data-testid={`mup-rinuncia-del-${r.id}`} onClick={() => delRinuncia(r.id)} disabled={busy}
                      className="text-xs text-red-500 hover:underline shrink-0">Rimuovi</button>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 mb-3">Nessuna annotazione.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <Input data-testid="mup-rinuncia-garanzia" value={rinG} onChange={(e) => setRinG(e.target.value)} placeholder="Garanzia" className="sm:col-span-2" />
              <Input data-testid="mup-rinuncia-data" type="date" value={rinData} onChange={(e) => setRinData(e.target.value)} />
              <Input data-testid="mup-rinuncia-motivo" value={rinMotivo} onChange={(e) => setRinMotivo(e.target.value)} placeholder="Motivo (facoltativo)" />
            </div>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <Button data-testid="mup-rinuncia-add-btn" size="sm" variant="outline" disabled={busy} onClick={addRinuncia}>
                <PenLine className="h-4 w-4 mr-1.5" /> Aggiungi annotazione
              </Button>
              {(p.garanzie_rinunciate || []).length > 0 && !p.rinunce_signature?.signed_at && (
                <Button data-testid="mup-apx-sign-btn" size="sm" disabled={busy} onClick={sendApxOtp} className="brand-gradient text-white border-0">
                  <PenLine className="h-4 w-4 mr-1.5" /> Invia OTP e fai firmare l'appendice
                </Button>
              )}
              {(p.garanzie_rinunciate || []).length > 0 && !p.rinunce_signature?.signed_at && (
                <Button data-testid="mup-apx-link-btn" size="sm" variant="outline" disabled={busy} onClick={prepareApxLink}>
                  <Send className="h-4 w-4 mr-1.5" /> Copia link firma cliente
                </Button>
              )}
            </div>
            {p.rinunce_signature?.signed_at && (
              <p className="text-xs text-amber-600 mt-2">Nota: modificando le annotazioni, la firma dell'appendice verrà invalidata e andrà rifatta.</p>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Sicurezza documento">
            <div className="text-sm space-y-2">
              <div><span className="text-slate-500">Hash bozza:</span><div className="font-mono text-xs break-all text-slate-700">{p.hash || "— (genera il PDF)"}</div></div>
              {p.signed_hash && <div><span className="text-slate-500">Hash firmato:</span><div className="font-mono text-xs break-all text-emerald-700">{p.signed_hash}</div></div>}
              {p.signature?.signed_at && (
                <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                  Firmato il {fmt(p.signature.signed_at)} · IP {p.signature.ip} · tx {p.signature.transaction_id?.slice(0, 10)}…
                </div>
              )}
            </div>
          </Card>

          <Card title="Timeline / Audit" icon={Clock}>
            <ol className="space-y-3">
              {(p.timeline || []).slice().reverse().map((e, i) => (
                <li key={i} data-testid={`mup-timeline-${i}`} className="relative pl-5">
                  <span className="absolute left-0 top-1.5 h-2 w-2 rounded-full bg-brand-blue" />
                  <div className="text-sm text-slate-900 capitalize">{e.action.replace("_", " ")}</div>
                  <div className="text-xs text-slate-500">{e.detail}</div>
                  <div className="text-[11px] text-slate-400">{fmt(e.at)} · {e.by}</div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Firma elettronica con OTP</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">Il cliente ha ricevuto un codice OTP via <b>SMS</b>{otpDest ? <> al numero <b>{otpDest}</b></> : ""}. Fatti dettare il codice e inseriscilo per completare la firma.</p>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="mup-sign-order">
              <p className="text-xs font-semibold text-blue-800 mb-1.5">Con un unico codice OTP il cliente firma l'intero pacchetto, nell'ordine:</p>
              <ol className="text-sm text-slate-700 space-y-0.5">
                <li><b>1)</b> Informativa Privacy (Reg. UE 2016/679) e consensi</li>
                <li><b>2)</b> Modulo Unico Precontrattuale (MUP)</li>
                <li><b>3)</b> Ricevuta del contraente</li>
              </ol>
            </div>
            <Input data-testid="mup-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="Codice OTP a 6 cifre" maxLength={6} />
            <button type="button" onClick={resendOtp} disabled={resendIn > 0 || busy}
              className="text-sm text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline" data-testid="mup-otp-resend-btn">
              {resendIn > 0 ? `Reinvia SMS tra ${resendIn}s` : "Non hai ricevuto il codice? Reinvia SMS"}
            </button>
          </div>
          <DialogFooter>
            <Button data-testid="mup-sign-confirm-btn" disabled={busy || otp.length < 6} onClick={sign} className="brand-gradient text-white border-0">
              Conferma firma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={apxOpen} onOpenChange={setApxOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Firma appendice garanzie con OTP</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">Il cliente ha ricevuto un codice OTP via <b>SMS</b>{apxDest ? <> al numero <b>{apxDest}</b></> : ""}. Fatti dettare il codice e inseriscilo per firmare l'appendice delle garanzie non inserite.</p>
            <Input data-testid="mup-apx-otp-input" value={apxOtp} onChange={(e) => setApxOtp(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="Codice OTP a 6 cifre" maxLength={6} />
            <button type="button" onClick={resendApxOtp} disabled={apxResendIn > 0 || busy}
              className="text-sm text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline" data-testid="mup-apx-otp-resend-btn">
              {apxResendIn > 0 ? `Reinvia SMS tra ${apxResendIn}s` : "Non hai ricevuto il codice? Reinvia SMS"}
            </button>
          </div>
          <DialogFooter>
            <Button data-testid="mup-apx-sign-confirm-btn" disabled={busy || apxOtp.length < 6} onClick={signApx} className="brand-gradient text-white border-0">
              Conferma firma appendice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MupLayout>
  );
}

const TRI = (v) => ({ si: "Sì", no: "No", na: "N/A", non_compilato: "—" }[v] || "—");

const dIT = (iso) => {
  const s = String(iso || "").trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

const YN = (v) => (
  <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs ${v ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-500"}`}>
    {v ? "✓" : "✗"}
  </span>
);

const Card = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
    <h3 className="font-heading text-base font-medium text-slate-900 mb-4 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-brand-blue" />} {title}
    </h3>
    {children}
  </div>
);

const KV = ({ icon: Icon, k, v }) => (
  <div className="flex items-center justify-between border-b border-slate-100 py-1.5">
    <span className="text-slate-500 flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />}{k}</span>
    <span className="text-slate-900 font-medium text-right">{v || "—"}</span>
  </div>
);
